import type { Autocomplete } from '@schibsted-nmp/search-frontend-clients/autocomplete';
import type { QueryFunctionContext } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { useAppConfig } from '../AppConfig';
import { getAutocompleteQueryKey } from './AutocompleteQuery';

const FIVE_SECONDS_IN_MILLISECONDS = 1000 * 5;

interface AutocompleteQueryMeta {
    apiUrl: string;
}

declare module '@tanstack/react-query' {
    // @ts-expect-error Duplicate identifier 'QueryMeta'.ts(2300)
    type QueryMeta = AutocompleteQueryMeta;
}

type AutocompleteDataParams = {
    rows: number;
    term: string;
};

export function useAutocompleteData({ rows, term }: AutocompleteDataParams) {
    const { apiUrl, searchKey } = useAppConfig();

    const hasSearchTerm = Boolean(term);

    const autocompleteResult = useQuery({
        enabled: hasSearchTerm,
        meta: {
            apiUrl,
        },
        placeholderData: hasSearchTerm
            ? (previousData) => previousData
            : undefined,
        staleTime: FIVE_SECONDS_IN_MILLISECONDS,
        queryFn: fetchAutocompleteData,
        queryKey: getAutocompleteQueryKey({ rows, searchKey, term }),
    });

    return {
        autocompleteData: autocompleteResult.data ?? [],
    };
}

type AutocompleteQueryKey = ReturnType<typeof getAutocompleteQueryKey>;

async function fetchAutocompleteData({
    meta,
    signal,
    queryKey,
}: QueryFunctionContext<AutocompleteQueryKey>): Promise<Autocomplete> {
    const { apiUrl } = meta ?? {};
    const [{ rows, searchKey, term }] = queryKey;

    const url = new URL(`${apiUrl}/autocomplete/${searchKey}`);
    const searchParams = new URLSearchParams({ rows: rows.toString(), term });
    url.search = searchParams.toString();

    try {
        const response = await fetch(url, { signal });
        if (!response.ok) {
            const message = await response.text();
            return Promise.reject(
                `Got status ${response.status} calling ${url}: ${message}`,
            );
        }
        return response.json();
    } catch (err) {
        return Promise.reject(
            new Error(`Error calling ${url}`, { cause: err }),
        );
    }
}
